import { IonContent, IonPage, IonAlert, IonList, IonItem, IonLabel, IonButton } from "@ionic/react"; // componentes de Ionic para la vista y la lista
import Header from "src/components/Header"; // encabezado reutilizable
import React from "react"; // React y hooks
import { useHistory } from "react-router-dom"; // navegación
import EvaluacionAPI from "src/services/evaluacion"; // servicio para consumir API de evaluaciones
import LoginAPI from "src/services/login"; // servicio para obtener datos del usuario autenticado
import Operarios from "src/services/operarios"; // servicio para obtener datos de operarios

type EvaluacionEnProceso = { // tipo auxiliar para la grilla de evaluaciones en proceso
  id: number; // id de la evaluación
  evaluado_id: number; // id/legajo del evaluado
  evaluador_id: number; // id del evaluador
  evaluador_cargo: string; // cargo del evaluador
  fecha_evaluacion: string; // fecha de creación/actualización
  porcentaje_final: number; // porcentaje parcial/final
  evaluado_nombre?: string; // nombre del evaluado (calculado en el front)
};

// Página de inicio del módulo: mostrar modal de selección de tipo y listado de evaluaciones en proceso
const EvaluacionDesempenoHome: React.FC = () => { // componente principal
  const [showTipoModal, setShowTipoModal] = React.useState<boolean>(false); // abrir el modal solo cuando el cargo ya esté cargado
  const [enProceso, setEnProceso] = React.useState<EvaluacionEnProceso[]>([]); // estado para evaluaciones en proceso
  const [cargando, setCargando] = React.useState<boolean>(false); // indicador de carga del listado
  const [miCedula, setMiCedula] = React.useState<number | null>(null); // cédula del usuario autenticado (evaluador actual)
  const [miCargo, setMiCargo] = React.useState<string>(""); // cargo del usuario autenticado (para reglas de negocio)
  const [cargoCargado, setCargoCargado] = React.useState<boolean>(false); // controlar cuándo ya tenemos el cargo disponible
  const history = useHistory(); // navegación entre rutas

  // Regla: solo FISCAL o LIDER AGENTIS pueden evaluar SUPERVISOR.
  // Nota: se usa contains para soportar variantes (p.ej. "FISCALIZADOR A" / "LIDER AGENTIS").
  const cargoUp = (miCargo || "").trim().toUpperCase();
  const esFiscalOAgentis = cargoUp.includes("FIS") || cargoUp.includes("AGENTIS");

  React.useEffect(() => { // cargar datos básicos del usuario autenticado
    LoginAPI.me() // invocar endpoint de información del usuario
      .then(({ data }) => { // procesar respuesta
        const u = data?.datos || {}; // extraer bloque de datos
        const ced = Number(u.emp_cedula || 0) || null; // convertir cédula a número o null
        setMiCedula(ced); // guardar cédula en estado para usar como filtro en las evaluaciones
        setMiCargo(String(u.emp_cargo || "")); // guardar cargo para limitar opciones según regla
      })
      .catch(() => {
        setMiCedula(null); // en caso de error, dejar null (no cargará evaluaciones en proceso)
        setMiCargo(""); // limpiar cargo en caso de error
      })
      .finally(() => {
        setCargoCargado(true); // marcar que ya se intentó cargar el cargo (con o sin éxito)
      });
  }, []); // sin dependencias para ejecutarse solo una vez

  // Abrir el modal solo cuando el cargo ya se haya cargado para que Ionic construya botones correctos
  React.useEffect(() => {
    if (cargoCargado) {
      setShowTipoModal(true); // abrir una sola vez al entrar, ya con el cargo disponible
    }
  }, [cargoCargado]);

  React.useEffect(() => { // cargar evaluaciones en proceso del evaluador autenticado
    if (!miCedula) { // si no tenemos cédula válida
      setEnProceso([]); // limpiar lista
      return; // salir sin llamar al backend
    }
    const cargar = async () => { // función asíncrona interna
      try {
        setCargando(true); // marcar inicio de carga
        const data = await EvaluacionAPI.listar({ estado: "EN_PROCESO", evaluador_id: miCedula }); // pedir solo evaluaciones EN_PROCESO del evaluador actual
        const base = (data.datos || []) as EvaluacionEnProceso[]; // normalizar lista base
        const conNombre = await Promise.all( // enriquecer cada fila con el nombre del evaluado
          base.map(async (row) => { // iterar por cada evaluación
            try {
              const detalle = await Operarios.obtenerPorId(row.evaluado_id).catch(() => null); // obtener datos del evaluado
              const nombre = detalle?.nombre || detalle?.usuario || ""; // extraer nombre legible si existe
              return { ...row, evaluado_nombre: nombre }; // devolver fila extendida con nombre
            } catch {
              return row; // ante error, devolver fila tal cual
            }
          })
        );
        setEnProceso(conNombre); // guardar lista enriquecida en estado
      } catch {
        setEnProceso([]); // ante error, dejar lista vacía
      } finally {
        setCargando(false); // finalizar indicador de carga
      }
    };
    cargar(); // ejecutar carga cuando cambia la cédula
  }, [miCedula]); // depender de la cédula para recargar cuando esté disponible

  return (
    <IonPage> {/* contenedor general */}
      <Header titulo="Evaluación de Desempeño" /> {/* título del módulo */}
      <IonContent id="evaluacion-de-desempeno-home" className="ion-padding"> {/* contenido principal */}
        <div className="container"> {/* contenedor bootstrap */}
          <div className="text-muted mb-2">Seleccione el tipo de evaluación para continuar o retome una evaluación en proceso.</div> {/* texto indicativo */}

          {/* Alerta pequeña de selección de tipo */}
          <IonAlert
            isOpen={showTipoModal} // abrir al entrar
            header="Tipo de Evaluación" // título de la alerta
            message="Seleccione una opción para iniciar la evaluación." // mensaje
            onDidDismiss={() => setShowTipoModal(false)} // cerrar si se cancela
            buttons={[ // botones de acción
              {
                text: 'OPERARIO', // etiqueta
                handler: () => { setShowTipoModal(false); history.push('/evaluacion-de-desempeno/nuevo'); } // navegar
              },
              // Regla: solo FISCAL o LIDER AGENTIS puede ver opción SUPERVISOR
              ...(esFiscalOAgentis
                ? [{
                    text: 'SUPERVISOR', // etiqueta
                    handler: () => { setShowTipoModal(false); history.push('/evaluacion-de-desempeno/nuevo?tipo=supervisor'); } // navegar
                  }]
                : []),
              {
                text: 'Cancelar', // cancelar
                role: 'cancel' // cerrar
              }
            ]}
          />

          <div className="mt-3"> {/* sección de evaluaciones en proceso */}
            <h6 style={{ fontWeight: 600 }}>Evaluaciones en Proceso</h6> {/* título de sección */}
            {cargando && <div className="text-muted mt-1">Cargando evaluaciones en proceso...</div>} {/* indicador de carga */}
            {!cargando && enProceso.length === 0 && (
              <div className="text-muted mt-1">No tenés evaluaciones en proceso.</div> // mensaje cuando no hay borradores
            )}
            {!cargando && enProceso.length > 0 && (
              <IonList>
                {enProceso.map((item) => (
                  <IonItem key={item.id} lines="none"> {/* fila para cada evaluación en proceso */}
                    <IonLabel className="ion-text-wrap">
                      <div><strong>Evaluado:</strong> {item.evaluado_nombre || item.evaluado_id}</div> {/* mostrar nombre del evaluado o id como fallback */}
                      <div><small>Evaluación ID: {item.id}</small></div> {/* mostrar id de la evaluación como dato secundario */}
                    </IonLabel>
                    <IonButton
                      slot="end"
                      size="small"
                      color="primary"
                      onClick={() => history.push(`/evaluacion-de-desempeno/nuevo?id=${item.id}`)}
                    >
                      Continuar
                    </IonButton> {/* botón para continuar edición */}
                  </IonItem>
                ))}
              </IonList>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default EvaluacionDesempenoHome; // exportar componente


